import * as React from 'react';
import { SelectOptions } from './types';
import { STATE } from 'app/constants';
import { FullscreenCodeSelector } from 'app/components';
import { replace, to } from 'app/area/shared/navigation';
import { compose } from 'redux';
import { Location } from 'history';
import { routeConstants, CodeSelectorFor } from 'app/routes/route-constants';
import { containerFactory, DefaultContainerProps } from 'app/infrastructure/redux';
import { actionCreators } from './actions';
import { codeSelectorTranslations } from 'app/area/code-selector/constants';
import { ListViewCodeSelector } from 'app/area/list-view-code-selector/list-view-code-selector';
import { CodetableAdministrationState } from 'app/area/codetable/types-store';
import { getBreadcrumbs } from 'app/area/shared/codetable/breadcrumbs';

type CodeSelectorProps = DefaultContainerProps & SelectOptions & typeof actionCreators & { breadcrumbs?: number[] };

interface CodeSelectorPropsForType {
  onOpen: () => void;
  title: string;
  infoMessage?: string;
  urlResolver: (value: number | number[]) => string | { pathname: string; query: Object };
  onClose: () => void;
}

/**
 * Support forwarding of query parameters to the selected route
 * @param location
 */
const includeQueryParams = (location: Location) => (route: string) => ({ pathname: route, query: location.query });

const codeSelectorPropsCreator = ({
  translation,
  router,
  loadConnectedCaseTypes,
  loadCaseTypes,
  loadCompanyReports,
  loadDashboards,
  loadCodetableAdministrationList
}: CodeSelectorProps): CodeSelectorPropsForType => ({
  [CodeSelectorFor.ConnectedCaseType]: {
    onOpen: () => loadConnectedCaseTypes(router.location.query.draftId, router.location.query.connectionTypeId),
    title: translation.select_case_type,
    urlResolver: compose(includeQueryParams(router.location), routeConstants.createCase),
    onClose: () => replace({
      pathname: routeConstants.draftViewFor(router.location.query.draftId),
      query: { section: router.location.query.returnToSection }
    })
  },
  [CodeSelectorFor.ChangeCaseType]: {
    onOpen: loadCaseTypes,
    title: translation.change_case_type,
    infoMessage: translation.change_case_type_info_message,
    urlResolver: compose(includeQueryParams(router.location), routeConstants.changeCaseTypeTo),
    onClose: () => replace({
      pathname: routeConstants.draftViewFor(router.location.query.draftId)
    })
  },
  [CodeSelectorFor.CaseType]: {
    onOpen: loadCaseTypes,
    title: translation.select_case_type,
    urlResolver: compose(includeQueryParams(router.location), routeConstants.createCase),
    onClose: () => replace(routeConstants.startupView)
  },
  [CodeSelectorFor.CompanyReport]: {
    onOpen: loadCompanyReports,
    title: translation.reports_company,
    urlResolver: routeConstants.companyReportFor,
    onClose: () => replace(routeConstants.startupView)
  },
  [CodeSelectorFor.Dashboard]: {
    onOpen: loadDashboards,
    title: translation.dashboards,
    urlResolver: routeConstants.dashboardFor,
    onClose: () => replace(routeConstants.startupView)
  },
  [CodeSelectorFor.CodetableAdministration]: {
    onOpen: loadCodetableAdministrationList,
    title: translation.codetable_administration,
    urlResolver: routeConstants.codetableAdministrationFor,
    onClose: () => replace(routeConstants.startupView)
  }
}[router.params.type]);

type CodeSelectorComponentType = React.FunctionComponent<CodeSelectorProps> & {
  statePath: string;
};

const shouldRenderListView = (type: keyof typeof CodeSelectorFor) => type === CodeSelectorFor.CodetableAdministration;

const CodeSelector: CodeSelectorComponentType = (props: CodeSelectorProps): JSX.Element => {
  const { items, fetching, translation, router, breadcrumbs, sharedState, checkCodetableAccess } = props;
  const selectorProps = codeSelectorPropsCreator(props);

  if (shouldRenderListView(router.params.type)) {
    return <ListViewCodeSelector
      adminPanelAccess={sharedState?.access?.adminPanel}
      checkCodetableAccess={checkCodetableAccess}
      onClose={selectorProps.onClose}
      fetching={fetching}
      options={items}
      routeHandler={to}
      breadcrumbs={breadcrumbs || []}
      translation={translation}
      {...selectorProps}
    />;
  }

  return <FullscreenCodeSelector
    onClose={selectorProps.onClose}
    fetching={fetching}
    options={items}
    textGoBack={translation.back}
    routeHandler={replace}
    {...selectorProps}
  />;
};

CodeSelector.statePath = STATE.codeSelector;

export const selectors = {
  breadcrumbs: (_: CodetableAdministrationState, props: DefaultContainerProps) => getBreadcrumbs(props.params)
};

const container = containerFactory(CodeSelector, actionCreators, selectors, codeSelectorTranslations);

export { container as CodeSelector };
